import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  FormControl,
  FormLabel,
  Select,
  VStack,
  HStack,
  Box,
  Flex,
  IconButton,
  Input,
  Text,
  Divider
} from '@chakra-ui/react';
import { AddIcon, DeleteIcon } from '@chakra-ui/icons';

const DynamicRuleModal = ({ isOpen, onClose, onSave, availableActions, existingPayers = [], existingCarriers = [] }) => {
  const [action, setAction] = useState('');
  
  // We start with a root AND group
  const [rootCondition, setRootCondition] = useState({
    operator: 'AND',
    rules: []
  });

  useEffect(() => {
    if (isOpen) {
      setAction('');
      setRootCondition({
        operator: 'AND',
        rules: [{ field: 'payer', operator: '==', value: '' }]
      });
    }
  }, [isOpen]);

  const handleAddRule = () => {
    setRootCondition(prev => ({
      ...prev,
      rules: [...prev.rules, { field: 'payer', operator: '==', value: '' }]
    }));
  };

  const handleRemoveRule = (index) => {
    setRootCondition(prev => ({
      ...prev,
      rules: prev.rules.filter((_, i) => i !== index)
    }));
  };

  const handleRuleChange = (index, key, val) => {
    setRootCondition(prev => {
      const newRules = [...prev.rules];
      newRules[index] = { ...newRules[index], [key]: val };
      
      // If switching to IN or NOT_IN, ensure value is an array (or comma separated string for input)
      if (key === 'operator' && (val === 'IN' || val === 'NOT_IN')) {
        if (!Array.isArray(newRules[index].value)) {
          newRules[index].value = newRules[index].value ? [newRules[index].value] : [];
        }
      } else if (key === 'operator' && (val === '==' || val === '!=' || val === 'CONTAINS')) {
        if (Array.isArray(newRules[index].value)) {
          newRules[index].value = newRules[index].value[0] || '';
        }
      }
      
      return { ...prev, rules: newRules };
    });
  };

  const handleValueChange = (index, val, operator) => {
    if (operator === 'IN' || operator === 'NOT_IN') {
      // Split by comma and trim for array operators
      const arr = val.split(',').map(s => s.trim()).filter(Boolean);
      handleRuleChange(index, 'value', arr);
    } else {
      handleRuleChange(index, 'value', val);
    }
  };

  const handleSubmit = () => {
    // Clean up empty rules and trim string values before saving
    const cleanRules = rootCondition.rules
      .map(r => ({
        ...r,
        value: Array.isArray(r.value)
          ? r.value.map(v => (typeof v === 'string' ? v.trim() : v)).filter(Boolean)
          : (typeof r.value === 'string' ? r.value.trim() : r.value)
      }))
      .filter(r => {
        if (Array.isArray(r.value)) return r.value.length > 0;
        return Boolean(r.value);
      });

    const finalCondition = {
      ...rootCondition,
      rules: cleanRules
    };

    const payload = {
      rule_type: 'dynamic', // Dynamic rules run in their own final phase
      target_name: 'DYNAMIC_RULE',
      action: action,
      conditions: finalCondition
    };
    
    onSave(payload);
  };

  const isFormValid = action && rootCondition.rules.length > 0 && rootCondition.rules.every(r => {
    if (Array.isArray(r.value)) return r.value.length > 0;
    return r.value && r.value.trim() !== '';
  });

  // Group actions for better UX
  const groupedActions = {
    'Remove Codes': availableActions.filter(a => a.startsWith('remove_')),
    'Change Codes': availableActions.filter(a => a.startsWith('change_') && !a.includes('modifier')),
    'Apply Modifiers': availableActions.filter(a => a.startsWith('apply_') || a.includes('modifier')),
    'Cleanups': availableActions.filter(a => a.startsWith('cleanup_')),
    'Other': availableActions.filter(a => !a.startsWith('remove_') && !a.startsWith('change_') && !a.startsWith('apply_') && !a.startsWith('cleanup_') && !a.includes('modifier'))
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Create Advanced Rule</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={6} align="stretch">
            
            <FormControl isRequired>
              <FormLabel>Action to Perform</FormLabel>
              <Select 
                value={action} 
                onChange={(e) => setAction(e.target.value)}
                placeholder="Select an action..."
              >
                {Object.entries(groupedActions).map(([groupName, actions]) => {
                  if (actions.length === 0) return null;
                  return (
                    <optgroup key={groupName} label={groupName}>
                      {actions.map(a => (
                        <option key={a} value={a}>{a}</option>
                      ))}
                    </optgroup>
                  );
                })}
              </Select>
            </FormControl>

            <Divider />

            <Box>
              <Flex justify="space-between" align="center" mb={3}>
                <FormLabel mb={0}>Conditions (When should this run?)</FormLabel>
                <Select 
                  size="sm" 
                  width="100px" 
                  value={rootCondition.operator}
                  onChange={(e) => setRootCondition(prev => ({ ...prev, operator: e.target.value }))}
                >
                  <option value="AND">Match ALL</option>
                  <option value="OR">Match ANY</option>
                </Select>
              </Flex>

              <Box bg="gray.50" p={4} borderRadius="md" borderWidth="1px">
                <VStack spacing={3} align="stretch">
                  {rootCondition.rules.map((rule, index) => (
                    <HStack key={index} spacing={2}>
                      <Select 
                        size="sm" 
                        value={rule.field} 
                        onChange={(e) => handleRuleChange(index, 'field', e.target.value)}
                        width="30%"
                      >
                        <option value="payer">Payer</option>
                        <option value="carrier">Carrier</option>
                      </Select>

                      <Select 
                        size="sm" 
                        value={rule.operator} 
                        onChange={(e) => handleRuleChange(index, 'operator', e.target.value)}
                        width="30%"
                      >
                        <option value="==">Equals</option>
                        <option value="!=">Does Not Equal</option>
                        <option value="CONTAINS">Contains</option>
                        <option value="IN">Is In List</option>
                        <option value="NOT_IN">Not In List</option>
                      </Select>

                      <Box flex="1">
                        {(rule.operator === 'IN' || rule.operator === 'NOT_IN') ? (
                          <Input 
                            size="sm" 
                            placeholder="Value1, Value2..." 
                            value={Array.isArray(rule.value) ? rule.value.join(', ') : rule.value}
                            onChange={(e) => handleValueChange(index, e.target.value, rule.operator)}
                          />
                        ) : (
                          <Input 
                            size="sm" 
                            placeholder="Value..." 
                            value={Array.isArray(rule.value) ? rule.value[0] || '' : rule.value}
                            onChange={(e) => handleValueChange(index, e.target.value, rule.operator)}
                            list={`suggestions-${index}`}
                          />
                        )}
                        <datalist id={`suggestions-${index}`}>
                          {(rule.field === 'carrier' ? existingCarriers : (existingPayers || [])).map(name => (
                            <option key={name} value={name} />
                          ))}
                        </datalist>
                      </Box>

                      <IconButton 
                        icon={<DeleteIcon />} 
                        size="sm" 
                        colorScheme="red" 
                        variant="ghost"
                        onClick={() => handleRemoveRule(index)}
                        isDisabled={rootCondition.rules.length === 1}
                      />
                    </HStack>
                  ))}
                  
                  <Button 
                    size="sm" 
                    leftIcon={<AddIcon />} 
                    onClick={handleAddRule} 
                    alignSelf="flex-start"
                    variant="ghost"
                    colorScheme="blue"
                  >
                    Add Condition
                  </Button>
                </VStack>
              </Box>
            </Box>

          </VStack>
        </ModalBody>

        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onClose}>
            Cancel
          </Button>
          <Button 
            colorScheme="blue" 
            onClick={handleSubmit}
            isDisabled={!isFormValid}
          >
            Save Advanced Rule
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default DynamicRuleModal;
